import { useState } from 'react';
import { Send, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '@/i18n/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ContactForm = () => {
  const { t } = useLanguage();

  const interests = [
    { key: 'smartLockers', label: t('interests.smartLockers') },
    { key: 'smartMetering', label: t('interests.smartMetering') },
    { key: 'assetTracking', label: t('interests.assetTracking') },
    { key: 'elderlyCare', label: t('interests.elderlyCare') },
    { key: 'airQuality', label: t('interests.airQuality') },
    { key: 'energyMonitoring', label: t('interests.energyMonitoring') },
    { key: 'officeManagement', label: t('interests.officeManagement') },
    { key: 'connectivity', label: t('interests.connectivity') },
    { key: 'tektelic', label: t('interests.tektelic') },
    { key: 'consulting', label: t('interests.consulting') },
  ];

  const countries = [
    { key: 'lithuania', label: t('countries.lithuania') },
    { key: 'latvia', label: t('countries.latvia') },
    { key: 'estonia', label: t('countries.estonia') },
    { key: 'poland', label: t('countries.poland') },
    { key: 'finland', label: t('countries.finland') },
    { key: 'sweden', label: t('countries.sweden') },
    { key: 'germany', label: t('countries.germany') },
    { key: 'other', label: t('countries.other') },
  ];

  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    country: '',
    interests: [],
    message: '',
    is_city: false,
    is_integrator: false,
    agree_contact: false,
    agree_newsletter: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckbox = (name, checked) => {
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  const handleInterestChange = (interestKey, checked) => {
    setFormData(prev => ({
      ...prev,
      interests: checked 
        ? [...prev.interests, interestKey]
        : prev.interests.filter(i => i !== interestKey)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.agree_contact) {
      setError(t('contact.errorAgree'));
      return;
    }

    if (!formData.name || !formData.email || !formData.company || formData.interests.length === 0) {
      setError(t('contact.errorRequired'));
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        name: formData.name,
        company: formData.company,
        email: formData.email,
        phone: formData.phone || '',
        country: formData.country || '',
        interest: formData.interests.join(', '),
        message: formData.message || '',
        is_city: formData.is_city,
        is_integrator: formData.is_integrator,
        newsletter: formData.agree_newsletter,
      };

      await axios.post(`${API}/leads`, payload);
      
      setIsSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (err) {
      console.error('Form submission error:', err);
      setError(t('contact.errorSubmit'));
      toast.error('Failed to send message');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <section id="contact" className="py-20 md:py-32 bg-white" data-testid="contact-section">
        <div className="container-0g">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-teal-600/10 flex items-center justify-center">
              <CheckCircle className="h-10 w-10 text-teal-600" />
            </div>
            <h2 className="text-3xl font-semibold text-slate-900 mb-4">
              {t('contact.thankYouTitle')}
            </h2>
            <p className="text-lg text-slate-600">
              {t('contact.thankYouMessage')}
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" className="py-20 md:py-32 bg-white" data-testid="contact-section">
      <div className="container-0g">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left - Info */}
          <div>
            <p className="section-label">{t('contact.label')}</p>
            <h2 className="section-title mb-6">{t('contact.title')}</h2>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed">
              {t('contact.subtitle')}
            </p>

            {/* Contact details */}
            <div className="space-y-4 mb-8">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-sm bg-slate-100 flex items-center justify-center">
                  <Send className="h-5 w-5 text-teal-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">{t('contact.email')}</p>
                  <a href="mailto:info@0gbaltics.com" className="text-slate-900 font-medium hover:text-teal-600">
                    info@0gbaltics.com
                  </a>
                </div>
              </div>
            </div>

            {/* What to expect */}
            <div className="p-6 bg-slate-50 border border-slate-200 rounded-sm">
              <h4 className="font-semibold text-slate-900 mb-3">{t('contact.whatToExpect')}</h4>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start space-x-2">
                  <span className="text-teal-600 mt-1">•</span>
                  <span>{t('contact.expect1')}</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-teal-600 mt-1">•</span>
                  <span>{t('contact.expect2')}</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-teal-600 mt-1">•</span>
                  <span>{t('contact.expect3')}</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Right - Form */}
          <div className="bg-slate-50 p-6 md:p-8 border border-slate-200 rounded-sm">
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Name */}
              <div>
                <Label htmlFor="name" className="text-slate-700">{t('contact.fullName')} *</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Smith"
                  className="mt-1 form-input"
                  required
                  data-testid="input-name"
                />
              </div>

              {/* Company */}
              <div>
                <Label htmlFor="company" className="text-slate-700">{t('contact.company')} *</Label>
                <Input
                  id="company"
                  name="company"
                  type="text"
                  value={formData.company}
                  onChange={handleChange}
                  placeholder="Your Company"
                  className="mt-1 form-input"
                  required
                  data-testid="input-company"
                />
              </div>

              {/* Email & Phone row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email" className="text-slate-700">{t('contact.email')} *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@company.com"
                    className="mt-1 form-input"
                    required
                    data-testid="input-email"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-slate-700">{t('contact.phone')}</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+370 600 00000"
                    className="mt-1 form-input"
                    data-testid="input-phone"
                  />
                </div>
              </div>

              {/* Country */}
              <div>
                <Label htmlFor="country" className="text-slate-700">{t('contact.country')}</Label>
                <Select
                  value={formData.country}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, country: value }))}
                >
                  <SelectTrigger className="mt-1" data-testid="select-country">
                    <SelectValue placeholder={t('contact.selectCountry')} />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map((country) => (
                      <SelectItem 
                        key={country.key} 
                        value={country.label}
                        data-testid={`country-option-${country.key}`}
                      >
                        {country.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Interests - Checkboxes */}
              <div>
                <Label className="text-slate-700 mb-3 block">{t('contact.interest')} *</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 p-4 bg-white border border-slate-200 rounded-sm max-h-48 overflow-y-auto">
                  {interests.map((interest) => (
                    <div key={interest.key} className="flex items-center space-x-2">
                      <Checkbox
                        id={`interest-${interest.key}`}
                        checked={formData.interests.includes(interest.key)}
                        onCheckedChange={(checked) => handleInterestChange(interest.key, checked)}
                        data-testid={`checkbox-interest-${interest.key}`}
                      />
                      <Label 
                        htmlFor={`interest-${interest.key}`} 
                        className="text-sm text-slate-600 cursor-pointer font-normal"
                      >
                        {interest.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Message */}
              <div>
                <Label htmlFor="message" className="text-slate-700">{t('contact.message')}</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={t('contact.messagePlaceholder')}
                  className="mt-1 form-input min-h-[120px]"
                  data-testid="input-message"
                />
              </div>

              {/* Checkboxes */}
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is_city"
                    checked={formData.is_city}
                    onCheckedChange={(checked) => handleCheckbox('is_city', checked)}
                    data-testid="checkbox-city"
                  />
                  <Label htmlFor="is_city" className="text-sm text-slate-600 cursor-pointer">
                    {t('contact.checkCity')}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is_integrator"
                    checked={formData.is_integrator}
                    onCheckedChange={(checked) => handleCheckbox('is_integrator', checked)}
                    data-testid="checkbox-integrator"
                  />
                  <Label htmlFor="is_integrator" className="text-sm text-slate-600 cursor-pointer">
                    {t('contact.checkIntegrator')}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="agree_newsletter"
                    checked={formData.agree_newsletter}
                    onCheckedChange={(checked) => handleCheckbox('agree_newsletter', checked)}
                    data-testid="checkbox-newsletter"
                  />
                  <Label htmlFor="agree_newsletter" className="text-sm text-slate-600 cursor-pointer">
                    {t('contact.checkNewsletter')}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="agree_contact"
                    checked={formData.agree_contact}
                    onCheckedChange={(checked) => handleCheckbox('agree_contact', checked)}
                    data-testid="checkbox-agree"
                  />
                  <Label htmlFor="agree_contact" className="text-sm text-slate-600 cursor-pointer">
                    {t('contact.checkAgree')} *
                  </Label>
                </div>
              </div>

              {/* Error message */}
              {error && (
                <div className="flex items-center space-x-2 text-red-600 text-sm" data-testid="form-error">
                  <AlertCircle className="h-4 w-4" />
                  <span>{error}</span>
                </div>
              )}

              {/* Submit */}
              <Button
                type="submit"
                className="w-full btn-primary"
                disabled={isSubmitting}
                data-testid="submit-button"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('contact.sending')}
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    {t('contact.submit')}
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
